/**
 * The last way the user interacted with the page — keyboard navigation or
 * a pointer — as `data-focus-modality` on the root element (TTTL-186).
 *
 * Browsers make text fields match :focus-visible even when focused with a
 * mouse click (typing needs the caret), so the global focus ring would
 * light up on every clicked input. GlobalStyles gates the ring for text
 * entry on this attribute instead, so a clicked field shows nothing and a
 * Tab-reached one shows the ring, like every other control.
 */

export type FocusModality = "keyboard" | "pointer";

const TEXT_ENTRY =
  'input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]';

/** A key press that counts as keyboard navigation; undefined otherwise.
 *  `withCommand` says Ctrl or Meta was held: a shortcut (Cmd+S opening
 *  the save modal, Cmd+K the palette) is not navigation, so it leaves the
 *  modality as it was — the field it focuses shows a ring only when the
 *  user was already moving through the page by keyboard.
 *
 *  Why the app keeps its own notion rather than trusting :focus-visible
 *  alone: a browser carries keyboard mode across a pointer click when the
 *  click never moves focus itself. The library's dropdown trigger cancels
 *  its mousedown, focuses the menu by script, and on a pointer pick hands
 *  focus back to the trigger by script — so after any typing in the page
 *  the trigger lights up with a ring the user never asked for. */
export function modalityAfterKey(
  key: string,
  targetIsTextEntry: boolean,
  withCommand = false,
): FocusModality | undefined {
  if (key === "Tab") return "keyboard";
  if (withCommand) return undefined;
  // Enter and Space activate a control — the next focus (a modal's first
  // field, say) came from the keyboard. Arrows, Home and End move focus
  // through tabs, menus and radio groups. Typed into a field they are
  // text or caret movement.
  if (targetIsTextEntry) return undefined;
  return NAVIGATION_KEYS.has(key) ? "keyboard" : undefined;
}

const NAVIGATION_KEYS = new Set([
  "Enter",
  " ",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
]);

export function installFocusModality(doc: Document): () => void {
  const set = (modality: FocusModality) => {
    doc.documentElement.dataset.focusModality = modality;
  };
  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as Element | null;
    const modality = modalityAfterKey(
      event.key,
      Boolean(target?.matches?.(TEXT_ENTRY)),
      event.ctrlKey || event.metaKey,
    );
    if (modality) set(modality);
  };
  const onPointerDown = () => set("pointer");
  doc.addEventListener("keydown", onKeyDown, true);
  doc.addEventListener("pointerdown", onPointerDown, true);
  return () => {
    doc.removeEventListener("keydown", onKeyDown, true);
    doc.removeEventListener("pointerdown", onPointerDown, true);
  };
}
